import * as React from 'react';
import { values } from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Button, Input, Label, FormGroup, CustomInput, Alert } from 'reactstrap';
import { Link } from 'react-router-dom';
import { State } from '../../redux/state';
import { Competition, Entity } from '../../userApi';
import { LProps, withLocalization } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import { makeSimpleRoutePath, Routes } from '../../utils/routerHelper';
import { logoPendingApproval, departmentDivisionFromEntity, isHiddenFromScoreboard } from '../../utils/competitionLabels';
import CcsfHeroSlideshow from './CcsfHeroSlideshow';
import {
  CCSF_CONTACT_EMAILS,
  CCSF_CONTACT_FAQ_ANSWER,
  CCSF_CONTACT_MAILTO,
  CCSF_OTHER_DEPARTMENT_BULLET,
  CCSF_WHO_CAN_JOIN_DETAILS
} from '../../utils/ccsfContacts';
import './ccsfPrelaunch.scss';

interface Props {
  competition: Competition;
  divisions: Entity[];
  apiBase: string;
  authOverlayOpen?: boolean;
  /** When true, Challenge has started — keep branded layout, use account signup CTAs. */
  launched?: boolean;
  isLoggedIn?: boolean;
  hasJoined?: boolean;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  wantsUpdates: boolean;
  wantsLaunchReminder: boolean;
  submitting: boolean;
  success: boolean;
  error: string;
  ics: string;
}

const PUBLIC_NAME = 'City and County of San Francisco Sustainability Challenge 2026';
const PAGE_TITLE = 'Sustainability Challenge 2026 | City and County of San Francisco';

const FAQ_PRELAUNCH = [
  {
    q: 'What is the Sustainability Challenge?',
    a: 'A month-long challenge for City and County of San Francisco staff. Take everyday sustainability actions, earn points, and contribute to your Division’s total.'
  },
  {
    q: 'How do I earn points?',
    a: 'Complete sustainability actions in the Challenge — from reducing waste and saving energy to sustainable commuting and food choices. Each action has a point value, and repeats may earn additional points.'
  },
  {
    q: 'Do I need to sign up again on October 1?',
    a: 'No. You will still create or log into your Challenge account when the competition opens. Signing up here puts you on the notification list and sends you a calendar invite so you are ready on day one.'
  },
  {
    q: 'How do Departments and Divisions work?',
    a: 'Choose your Division when you sign up. Your points count for your Division, and Divisions roll up to their Department on the scoreboard.'
  },
  {
    q: 'Who can join?',
    a: CCSF_WHO_CAN_JOIN_DETAILS
  },
  {
    q: 'Who can I contact with questions?',
    a: CCSF_CONTACT_FAQ_ANSWER
  }
];

const FAQ_LAUNCHED = [
  {
    q: 'What is the Sustainability Challenge?',
    a: 'A month-long challenge for City and County of San Francisco staff. Take everyday sustainability actions, earn points, and contribute to your Division’s total.'
  },
  {
    q: 'How do I earn points?',
    a: 'Complete sustainability actions in the Challenge — from reducing waste and saving energy to sustainable commuting and food choices. Each action has a point value, and repeats may earn additional points.'
  },
  {
    q: 'I preregistered — what do I do now?',
    a: 'Create your Challenge account (or log in if you already have one), choose your Division, and start completing actions. Preregistration put you on the notification list; you still need an account to earn points.'
  },
  {
    q: 'How do Departments and Divisions work?',
    a: 'Choose your Division when you sign up. Your points count for your Division, and Divisions roll up to their Department on the scoreboard.'
  },
  {
    q: 'Who can join?',
    a: CCSF_WHO_CAN_JOIN_DETAILS
  },
  {
    q: 'Who can I contact with questions?',
    a: CCSF_CONTACT_FAQ_ANSWER
  }
];

class CcsfPrelaunchLanding extends React.Component<LProps<Props>, FormState> {
  public state: FormState = {
    firstName: '',
    lastName: '',
    email: '',
    department: '',
    wantsUpdates: true,
    wantsLaunchReminder: false,
    submitting: false,
    success: false,
    error: '',
    ics: ''
  };

  private errorRef: HTMLDivElement | null = null;
  private successRef: HTMLDivElement | null = null;
  private previousTitle: string = '';

  public componentDidMount() {
    this.previousTitle = document.title;
    document.title = PAGE_TITLE;
  }

  public componentWillUnmount() {
    if (this.previousTitle) {
      document.title = this.previousTitle;
    }
  }

  public componentDidUpdate(_prevProps: LProps<Props>, prevState: FormState) {
    if (this.state.error && this.state.error !== prevState.error && this.errorRef) {
      this.errorRef.focus();
    }
    if (this.state.success && !prevState.success && this.successRef) {
      this.successRef.focus();
    }
  }

  private prefersReducedMotion = () => {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  private scrollToSignup = () => {
    const el = document.getElementById('ccsf-signup');
    if (el) {
      el.scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
      const first = document.getElementById('ccsf-first');
      if (first) {
        first.focus();
      }
    }
  }

  private scrollToHow = () => {
    const el = document.getElementById('ccsf-how');
    if (el) {
      el.scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
    }
  }

  private normalizeIcsForDownload = (ics: string): string => {
    const normalized = ics.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
    if (!normalized) {
      return '';
    }
    return normalized.split('\n').join('\r\n') + '\r\n';
  }

  private downloadIcs = () => {
    const { ics } = this.state;
    const body = this.normalizeIcsForDownload(ics);
    if (!body) {
      return;
    }
    const blob = new Blob([body], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ccsf-challenge-opens.ics';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const { apiBase, competition } = this.props;
    const { firstName, lastName, email, department, wantsUpdates, wantsLaunchReminder } = this.state;
    this.setState({ submitting: true, error: '' });
    try {
      const response = await fetch(
        `${apiBase}/competitions/${competition.name}/preregister`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            department,
            wantsUpdates,
            wantsLaunchReminder
          })
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = payload && payload[0] && payload[0].message
          ? payload[0].message
          : 'We could not complete your signup. Please try again.';
        this.setState({ submitting: false, error: message });
        return;
      }
      this.setState({
        submitting: false,
        success: true,
        ics: payload.ics || '',
        error: ''
      });
    } catch (_err) {
      this.setState({
        submitting: false,
        error: 'Network error. Please try again in a moment.'
      });
    }
  }

  public render() {
    const { t, divisions, competition, authOverlayOpen, launched, isLoggedIn, hasJoined } = this.props;
    const {
      firstName, lastName, email, department, wantsUpdates, wantsLaunchReminder,
      submitting, success, error, ics
    } = this.state;
    const title = (competition && competition.description) || PUBLIC_NAME;
    const pendingLogo = logoPendingApproval(competition);
    const logo = (!pendingLogo && competition && competition.logoUrl) ? competition.logoUrl : '';
    const domains = (competition && competition.permittedDomains) || ['sfgov.org', 'flysfo.com', 'sfdpw.org'];
    const publicDomains = domains.filter((d) => d !== 'example.test');
    const grouped = groupDivisions(divisions);
    const signupPath = makeSimpleRoutePath(Routes.Signup, t);
    const loginPath = makeSimpleRoutePath(Routes.Login, t);
    const joinPath = makeSimpleRoutePath(Routes.Join, t);
    const needsJoin = Boolean(isLoggedIn && !hasJoined);
    const primaryCtaLabel = needsJoin
      ? t('Join now')
      : launched ? t('Sign up') : t('Sign up for launch');
    const primaryCtaPath = needsJoin ? joinPath : signupPath;
    const faq = launched ? FAQ_LAUNCHED : FAQ_PRELAUNCH;
    const howSteps = launched
      ? [
          ['1', 'Create your account', 'Sign up with your work email and choose your Division.'],
          ['2', 'Introduce yourself', 'Complete your first action to unlock the rest of the Challenge catalog.'],
          ['3', 'Take sustainability actions', 'Complete actions at work, at home, and in your community.'],
          ['4', 'Earn points and climb the board', 'Help your Division rise on the leaderboard.']
        ]
      : [
          ['1', 'Sign up', 'Join the notification list now so you are ready on day one.'],
          ['2', 'Choose your Division', 'Select your Department’s Division when you create your account.'],
          ['3', 'Take sustainability actions', 'Complete actions at work, at home, and in your community starting with estimating your personal carbon footprint and introducing yourself.'],
          ['4', 'Earn points and climb the board', 'Help your Division rise on the leaderboard.']
        ];
    const keyDetails = launched
      ? [
          ['Status', 'Open now — October 1–30, 2026'],
          ['Ends', 'October 30, 2026 at 6:00 PM Pacific'],
          ['Who can join', CCSF_WHO_CAN_JOIN_DETAILS],
          ['How points work', 'Complete sustainability actions to earn points'],
          ['How you join', 'Create an account, choose your Division — open self-selection, no approval needed']
        ]
      : [
          ['Opens', 'October 1, 2026'],
          ['Ends', 'October 30, 2026 at 6:00 PM Pacific'],
          ['Who can join', CCSF_WHO_CAN_JOIN_DETAILS],
          ['How points work', 'Complete sustainability actions to earn points'],
          ['How you join', 'Choose your Division — open self-selection, no approval needed']
        ];

    return (
      <div className="ccsf-prelaunch" aria-hidden={authOverlayOpen ? true : undefined}>
        <a className="ccsf-prelaunch__skip" href="#ccsf-main">
          {t('Skip to main content')}
        </a>
        <header className="ccsf-prelaunch__nav" role="banner">
          <div className="ccsf-prelaunch__nav-inner">
            {logo ? (
              <img src={logo} alt="City and County of San Francisco" className="ccsf-prelaunch__logo" />
            ) : (
              <strong className="ccsf-prelaunch__logo-text">{title}</strong>
            )}
            <nav className="ccsf-prelaunch__nav-actions" aria-label={t('Page')}>
              <button type="button" className="ccsf-prelaunch__link-btn" onClick={this.scrollToHow}>
                {t('How it works')}
              </button>
              {needsJoin ? (
                <Link to={joinPath}>
                  <Button color="primary" className="ccsf-prelaunch__cta-sm">
                    {primaryCtaLabel}
                  </Button>
                </Link>
              ) : (
                <React.Fragment>
                  <Link className="ccsf-prelaunch__text-link" to={loginPath}>
                    {t('Log in')}
                  </Link>
                  {launched ? (
                    <Link to={signupPath}>
                      <Button color="primary" className="ccsf-prelaunch__cta-sm">
                        {primaryCtaLabel}
                      </Button>
                    </Link>
                  ) : (
                    <Button color="primary" className="ccsf-prelaunch__cta-sm" onClick={this.scrollToSignup}>
                      {primaryCtaLabel}
                    </Button>
                  )}
                </React.Fragment>
              )}
            </nav>
          </div>
        </header>

        <main id="ccsf-main">
          <section className="ccsf-prelaunch__hero" aria-labelledby="ccsf-hero-heading">
            <CcsfHeroSlideshow />
            <div className="ccsf-prelaunch__hero-inner">
              <p className="ccsf-prelaunch__eyebrow">{t('City and County of San Francisco')}</p>
              <h1 id="ccsf-hero-heading">{t('Sustainability Challenge 2026')}</h1>
              <p className="ccsf-prelaunch__badge">
                {launched ? t('Open now · October 1–30, 2026') : t('October 1–30, 2026')}
              </p>
              <p className="ccsf-prelaunch__lede">
                {t('Take simple actions, earn points for your Division, and help build a more sustainable, healthier, and circular San Francisco.')}
              </p>
              <div className="ccsf-prelaunch__hero-ctas">
                {needsJoin || launched ? (
                  <Link to={primaryCtaPath}>
                    <Button color="light" size="lg">{primaryCtaLabel}</Button>
                  </Link>
                ) : (
                  <Button color="light" size="lg" onClick={this.scrollToSignup}>{primaryCtaLabel}</Button>
                )}
                <Button outline={true} color="light" size="lg" onClick={this.scrollToHow}>{t('See how it works')}</Button>
              </div>
            </div>
          </section>

          <section className="ccsf-prelaunch__section" id="ccsf-signup" aria-labelledby="ccsf-signup-heading">
            <div className="ccsf-prelaunch__wrap ccsf-prelaunch__signup">
              {launched ? (
                <React.Fragment>
                  <h2 id="ccsf-signup-heading">{needsJoin ? t('Finish joining') : t('Join the Challenge')}</h2>
                  <p>
                    {needsJoin
                      ? t('You already have an account from the pilot. Choose your Division to join the 2026 Challenge and start earning points.')
                      : t('Create your account with your work email, choose your Division, and start earning points today.')}
                  </p>
                  <div className="ccsf-prelaunch__hero-ctas" style={{ justifyContent: 'flex-start', marginTop: '1.25rem' }}>
                    {needsJoin ? (
                      <Link to={joinPath}>
                        <Button color="primary" size="lg">{t('Choose Division & join')}</Button>
                      </Link>
                    ) : (
                      <React.Fragment>
                        <Link to={signupPath}>
                          <Button color="primary" size="lg">{t('Create account')}</Button>
                        </Link>
                        <Link to={loginPath}>
                          <Button outline={true} color="primary" size="lg">{t('Log in')}</Button>
                        </Link>
                      </React.Fragment>
                    )}
                  </div>
                  {!needsJoin &&
                    <p className="ccsf-prelaunch__hint" style={{ marginTop: '1rem' }}>
                      {`Use your work email (${publicDomains.join(', ')})`}
                    </p>
                  }
                </React.Fragment>
              ) : (
              <React.Fragment>
              <h2 id="ccsf-signup-heading">{t('Get notified when the Challenge opens')}</h2>
              <p>{t('Open preregistration: sign up now for a calendar invite and a welcome email on October 1 at 7:00 AM Pacific.')}</p>
              {success ? (
                <div
                  className="ccsf-prelaunch__success"
                  role="status"
                  tabIndex={-1}
                  ref={(el) => { this.successRef = el; }}
                >
                  <h3>{t('You\'re on the list!')}</h3>
                  <p>
                    {t('We\'ll send you a calendar invite and a welcome email when the Challenge opens on October 1.')}
                  </p>
                  {ics &&
                    <div className="ccsf-prelaunch__ics-download">
                      <Button color="primary" onClick={this.downloadIcs}>
                        {t('Download calendar invite (.ics) and open with Outlook (classic)')}
                      </Button>
                      <p className="ccsf-prelaunch__ics-hint">
                        {t('Save the file, then open it with Outlook (classic). The new Outlook app may not import .ics files correctly—use Outlook (classic) or the calendar invite in your confirmation email.')}
                      </p>
                    </div>
                  }
                </div>
              ) : (
                <form onSubmit={this.submit} className="ccsf-prelaunch__form" noValidate={false}>
                  {error && (
                    <div
                      ref={(el) => { this.errorRef = el; }}
                      tabIndex={-1}
                    >
                      <Alert color="danger" id="ccsf-form-error" role="alert">
                        {error}
                      </Alert>
                    </div>
                  )}
                  <div className="ccsf-prelaunch__form-row">
                    <FormGroup>
                      <Label for="ccsf-first">{t('First name')}</Label>
                      <Input
                        id="ccsf-first"
                        name="given-name"
                        autoComplete="given-name"
                        value={firstName}
                        required={true}
                        aria-required={true}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? 'ccsf-form-error' : undefined}
                        onChange={(e) => this.setState({ firstName: e.target.value })}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label for="ccsf-last">{t('Last name')}</Label>
                      <Input
                        id="ccsf-last"
                        name="family-name"
                        autoComplete="family-name"
                        value={lastName}
                        required={true}
                        aria-required={true}
                        onChange={(e) => this.setState({ lastName: e.target.value })}
                      />
                    </FormGroup>
                  </div>
                  <FormGroup>
                    <Label for="ccsf-email">{t('Email')}</Label>
                    <Input
                      id="ccsf-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      required={true}
                      aria-required={true}
                      aria-describedby="ccsf-email-hint"
                      onChange={(e) => this.setState({ email: e.target.value })}
                    />
                    <small className="ccsf-prelaunch__hint" id="ccsf-email-hint">
                      {`Use your work email (${publicDomains.join(', ')})`}
                    </small>
                  </FormGroup>
                  <FormGroup>
                    <Label for="ccsf-dept">{t('Department – Division')}</Label>
                    <Input
                      id="ccsf-dept"
                      name="organization"
                      type="select"
                      value={department}
                      onChange={(e) => this.setState({ department: e.target.value })}
                    >
                      <option value="">{t('I\'m not sure yet')}</option>
                      {grouped.map((group) =>
                        <optgroup key={group.department} label={group.department}>
                          {group.divisions.map((item) =>
                            <option
                              key={item.id}
                              value={group.department === 'Other' || !item.division
                                ? item.name
                                : `${group.department} - ${item.division}`}
                            >
                              {item.division}
                            </option>
                          )}
                        </optgroup>
                      )}
                    </Input>
                  </FormGroup>
                  <FormGroup check={true}>
                    <CustomInput
                      type="checkbox"
                      id="ccsf-updates"
                      checked={wantsUpdates}
                      onChange={(e) => this.setState({ wantsUpdates: e.target.checked })}
                      label={t('Send me challenge updates')}
                    />
                  </FormGroup>
                  <Button color="primary" size="lg" disabled={submitting} type="submit" aria-busy={submitting}>
                    {submitting ? t('Submitting…') : t('Notify me when the challenge opens')}
                  </Button>
                </form>
              )}
              </React.Fragment>
              )}
            </div>
          </section>

          <section className="ccsf-prelaunch__section" aria-labelledby="ccsf-what-heading">
            <div className="ccsf-prelaunch__wrap">
              <h2 id="ccsf-what-heading">{t('What it is')}</h2>
              <p>
                {t('A friendly month-long challenge for City and County of San Francisco staff. Choose your Division, take actions that reduce environmental impact, earn points, and see how your Division and Department compare.')}
              </p>
              <p>
                {t('The Challenge runs October 1–30, 2026, ending at 6:00 PM Pacific.')}
              </p>
            </div>
          </section>

          <section className="ccsf-prelaunch__section ccsf-prelaunch__section--tint" id="ccsf-why" aria-labelledby="ccsf-why-heading">
            <div className="ccsf-prelaunch__wrap">
              <h2 id="ccsf-why-heading">{t('Why participate')}</h2>
              <div className="ccsf-prelaunch__cards">
                {[
                  ['Make an impact', 'Small actions add up — at work, at home, and in your community — toward a sustainable San Francisco.'],
                  ['Cheer on your Division', 'Your points count for your Division and roll up to your Department.'],
                  ['Keep it simple', 'Pick actions that fit your life. Track progress and see the carbon you avoid.'],
                  ['Win a Prize!', 'High scoring divisions will be recognized and may receive fun and quirky prizes for participating.']
                ].map((card) => (
                  <div className="ccsf-prelaunch__card ccsf-prelaunch__card--wide" key={card[0]}>
                    <h3>{t(card[0])}</h3>
                    <p>{t(card[1])}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="ccsf-prelaunch__section" id="ccsf-how" aria-labelledby="ccsf-how-heading">
            <div className="ccsf-prelaunch__wrap">
              <h2 id="ccsf-how-heading">{t('How it works')}</h2>
              <ol className="ccsf-prelaunch__steps">
                {howSteps.map((step) => (
                  <li className="ccsf-prelaunch__step" key={step[0]}>
                    <div className="ccsf-prelaunch__step-num" aria-hidden={true}>{step[0]}</div>
                    <h3>{t(step[1])}</h3>
                    <p>{t(step[2])}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          <section className="ccsf-prelaunch__section" aria-labelledby="ccsf-depts-heading">
            <div className="ccsf-prelaunch__wrap">
              <h2 id="ccsf-depts-heading">{t('Participating Departments and Divisions')}</h2>
              {grouped.length > 0 ? (
                <div className="ccsf-prelaunch__dept-groups">
                  {grouped.map((group) => (
                    <div className="ccsf-prelaunch__dept-group" key={group.department}>
                      <h3>{group.department}</h3>
                      <ul>
                        {group.divisions.map((item) =>
                          <li key={item.id}>
                            {group.department === 'Other'
                              ? t(CCSF_OTHER_DEPARTMENT_BULLET)
                              : item.division}
                          </li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="ccsf-prelaunch__muted">
                  {t('Participating Departments and Divisions will appear here once configured.')}
                </p>
              )}
            </div>
          </section>

          <section className="ccsf-prelaunch__section ccsf-prelaunch__section--tint" aria-labelledby="ccsf-details-heading">
            <div className="ccsf-prelaunch__wrap">
              <h2 id="ccsf-details-heading">{t('Key details')}</h2>
              <div className="ccsf-prelaunch__cards">
                {keyDetails.map((card) => (
                  <div className="ccsf-prelaunch__card" key={card[0]}>
                    <h3>{t(card[0])}</h3>
                    <p>{t(card[1])}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="ccsf-prelaunch__section ccsf-prelaunch__section--tint" aria-labelledby="ccsf-faq-heading">
            <div className="ccsf-prelaunch__wrap">
              <h2 id="ccsf-faq-heading">{t('FAQ')}</h2>
              <div className="ccsf-prelaunch__faq">
                {faq.map((item) => (
                  <details key={item.q}>
                    <summary>{t(item.q)}</summary>
                    <p>{t(item.a)}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <footer className="ccsf-prelaunch__footer">
          <div className="ccsf-prelaunch__wrap ccsf-prelaunch__footer-inner">
            <div>
              <p className="ccsf-prelaunch__presented">{t('Presented by San Francisco Environment')}</p>
              <strong>City and County of San Francisco</strong>
              <p>{t('Sustainability Challenge 2026')}</p>
              <a href={CCSF_CONTACT_MAILTO}>{CCSF_CONTACT_EMAILS}</a>
            </div>
            <a
              className="ccsf-prelaunch__powered"
              href="https://coolclimate.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              <p>{t('Powered by CoolClimate Network')}</p>
              <img
                src="/assets/ccsf/coolclimate-network-logo.png"
                alt="CoolClimate Network"
                className="ccsf-prelaunch__ccn-logo"
              />
            </a>
            <nav className="ccsf-prelaunch__footer-links" aria-label={t('Footer')}>
              <Link to={makeSimpleRoutePath(Routes.AboutSF, t)}>{t('About')}</Link>
              <Link to={makeSimpleRoutePath(Routes.Accessibility, t)}>{t('Accessibility')}</Link>
              <Link to={loginPath}>{t('Log in')}</Link>
            </nav>
          </div>
        </footer>
      </div>
    );
  }
}

const otherLast = (a: Entity, b: Entity) => {
  if (a.name === 'Other') return 1;
  if (b.name === 'Other') return -1;
  return a.name.localeCompare(b.name);
};

const groupDivisions = (divisions: Entity[]) => {
  const order: string[] = [];
  const byDept: { [dept: string]: Array<{ id: number; name: string; division: string }> } = {};
  divisions.slice().sort(otherLast).forEach((entity) => {
    const parsed = departmentDivisionFromEntity(entity);
    const department = parsed.department || entity.name || 'Other';
    if (!byDept[department]) {
      byDept[department] = [];
      order.push(department);
    }
    byDept[department].push({
      id: entity.id,
      name: entity.name,
      division: parsed.division || entity.name
    });
  });
  const deptOrder = order.filter((name) => name !== 'Other').concat(order.filter((name) => name === 'Other'));
  return deptOrder.map((department) => ({
    department,
    divisions: byDept[department]
  }));
};

const mapStateToProps = (state: State): Props => {
  const competition = state.competition;
  const divisions = values(state.entities || {})
    .filter((entity) => entity && entity.type === Entity.TypeEnum.Team)
    .filter((entity) => !isHiddenFromScoreboard(entity, competition))
    .sort(otherLast);
  return {
    competition,
    divisions,
    apiBase: userApiBasePath(state)
  };
};

export default compose(
  withLocalization('ccsfPrelaunch'),
  connect(mapStateToProps)
)(CcsfPrelaunchLanding) as any;
